import { Image } from '@carbon/icons-react';
import { Button, Modal, Pagination, RadioTile, SkeletonPlaceholder, TextInput, TileGroup } from '@carbon/react';
import { asProblem, EmptyState, problemMessage, useAdmin, type FieldEditorProps } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNotice } from '../common/Notices';
import { ImageUploader } from './ImageUploader';
import { canManageImages, canReadImages, listImages, imagesKey, type ImageAsset } from './imagesApi';
import { useImageText } from './imageText';
import './images.scss';

const PAGE_SIZE = 24;

/**
 * An image's address field (`logoUrl`, `imageUrl`...) filled by choosing an uploaded image or uploading one (FR-ONL-17).
 * The address can still be typed. The images shown are the form's brand's and those for every brand.
 */
export function ImageField({ field, value, invalidText, values, onChange }: FieldEditorProps) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const [open, setOpen] = useState(false);
  const url = typeof value === 'string' ? value : '';
  const brandId = typeof values.brandId === 'string' && values.brandId ? values.brandId : null;
  const id = `field-${field.key}`;

  return (
    <div className="pos-image-field">
      <TextInput
        id={id}
        labelText={t(field.label)}
        helperText={field.helper ? t(field.helper) : undefined}
        required={Boolean(field.required)}
        invalid={Boolean(invalidText)}
        invalidText={invalidText}
        value={url}
        onChange={(e) => onChange(e.target.value || null)}
      />
      <div className="pos-image-field__row">
        {url && <img className="pos-images__thumb" src={url} alt={t('images.picker.preview', { label: t(field.label) })} />}
        {canReadImages(session) && (
          <Button kind="tertiary" renderIcon={Image} onClick={() => setOpen(true)}>
            {t('images.picker.choose')}
          </Button>
        )}
      </div>
      {open && (
        <ImagePickerModal
          title={t(field.label)}
          brandId={brandId}
          current={url}
          onClose={() => setOpen(false)}
          onPick={(image) => {
            onChange(image.url);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** Pick one of the uploaded images, or upload one, which is then picked. */
export function ImagePickerModal({
  title,
  brandId,
  current,
  onClose,
  onPick,
}: {
  title: string;
  brandId: string | null;
  current?: string;
  onClose(): void;
  onPick(image: ImageAsset): void;
}) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const text = useImageText();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ImageAsset>();
  const list = useQuery({
    queryKey: [...imagesKey, { brand: brandId ?? '' }, { page, pageSize: PAGE_SIZE }],
    queryFn: () => listImages({ brandId: brandId ?? undefined, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  const chosen = selected ?? items.find((p) => p.url === current);

  return (
    <Modal
      open
      size="lg"
      preventCloseOnClickOutside
      modalHeading={t('images.picker.title')}
      modalLabel={title}
      closeButtonLabel={t('carbon.close')}
      primaryButtonText={t('images.picker.use')}
      primaryButtonDisabled={!chosen}
      secondaryButtonText={t('common.cancel')}
      onRequestClose={onClose}
      onRequestSubmit={() => chosen && onPick(chosen)}
    >
      <div className="pos-images__picker">
        {canManageImages(session) && (
          <ImageUploader brandId={brandId} multiple={false} headingLevel={3} onUploaded={onPick} />
        )}
        {list.error && (
          <ErrorNotice title={problemMessage(t, asProblem(list.error))} onRetry={() => void list.refetch()} />
        )}
        {list.isLoading && (
          <div className="pos-images__grid-skeleton" aria-label={t('common.loading')}>
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonPlaceholder key={i} className="pos-images__tile-skeleton" />
            ))}
          </div>
        )}
        {list.data && items.length === 0 && (
          <EmptyState
            headingLevel={3}
            heading={t('admin.emptyTitle')}
            description={t(canManageImages(session) ? 'images.picker.empty' : 'images.emptyReadOnly')}
          />
        )}
        {items.length > 0 && (
          <TileGroup<string>
            name="image"
            legend={t('images.picker.legend')}
            className="pos-images__grid"
            valueSelected={chosen?.id ?? ''}
            onChange={(value) => setSelected(items.find((p) => p.id === value))}
          >
            {items.map((p) => (
              <RadioTile key={p.id} id={`image-${p.id}`} value={p.id} className="pos-images__tile">
                <img className="pos-images__tile-image" src={p.url} alt={p.altText ?? ''} loading="lazy" />
                <span className="pos-images__tile-name">{text.name(p)}</span>
                <span className="pos-images__muted">{text.size(p)}</span>
              </RadioTile>
            ))}
          </TileGroup>
        )}
        {Number(list.data?.total ?? 0) > PAGE_SIZE && (
          <Pagination
            itemsPerPageText={t('carbon.itemsPerPage')}
            itemRangeText={(min, max, total) => t('carbon.itemRange', { min, max, total })}
            pageRangeText={(_current, total) => t('carbon.pageRange', { total })}
            backwardText={t('carbon.previousPage')}
            forwardText={t('carbon.nextPage')}
            pageNumberText={t('carbon.pageNumber')}
            page={page}
            pageSize={PAGE_SIZE}
            pageSizes={[PAGE_SIZE]}
            pageSizeInputDisabled
            totalItems={Number(list.data?.total ?? 0)}
            onChange={({ page: next }) => setPage(next)}
          />
        )}
      </div>
    </Modal>
  );
}
